import { Application, LoanCategory } from '../../../../../../types/api';

import loanFormModel from './loanFormModel';
import {
  AdditionalDocumentsValidation,
  MeetingMinutesValidation,
  CurrentOccupantLedgerValidation,
  HoaBylawsValidation,
  MemberAssessmentPolicyValidation,
  PriorYearBudgetsValidation,
  LeaseOfTheSystemPropertyValidation,
  OfftakerPPAorLeaseAgreementValidation,
  ProofOfOfftakerCreditworthinessValidation,
  OffTakerLeaseOrProofOfOwnershipValidation,
  OffTakerUtilityBillValidation,
  PpaLeaseFinancialProjectionsValidation,
  ApprovalForCommunitySolarDeliveryValidation,
  CommunitySolarFinancialProjectionsValidation,
  SubscriberManagementCompanyContractValidation,
  PrincipalsValidation,
  ArticlesOfIncorporationValidation,
  NonProfitBoardResolutionValidation,
  ReligiousOrganizationAttendanceValidation,
  NonProfitBylawsCopyValidation,
  PropertyCompanyRentRollValidation,
  PropertyCompanyTenantMoveInYearValidation,
  PropertyCompanyTenantMonthlyRentPaymentValidation,
  PropertyCompanyTenantRenewalYearValidation,
  PropertyCompanyTenantRenewalLengthValidation,
  PropertyCompanyUploadRentRollOrProvideTenantInfoValidation,
  PropertyCompanyTenantNameValidation,
  PropertyCompanyTenantPaymentsSatisfactoryValidation,
  PgMortgageStatementValidation,
} from '../../../validations/required_documents';

const {
  loanCategory,
  borrowerIsReligiousOrganization,
  nonProfitRecievedBoardApproval,
  propertyCompanyUploadRentRollOrProvideTenantInfo,
  borrowerBusinessEstablishedYear,
  systemPropertyOwnershipStatus,
  systemPropertyCurrentlyPayingMortgage,
  // Tenant Info
  propertyCompanyTenantName,
  propertyCompanyTenantMoveInYear,
  propertyCompanyTenantRenewalYear,
  propertyCompanyTenantRenewalLength,
  propertyCompanyTenantMonthlyRentPayment,
  propertyCompanyTenantPaymentsSatisfactory,

  isPG,
} = loanFormModel.formField;

const {
  articlesOfIncorporation,
  nonProfitBoardResolution,
  nonProfitBylawsCopy,
  pgMortgageStatement,
  propertyCompanyRentRoll,
  religiousOrganizationAttendance,
} = loanFormModel.formField.companyDocuments;

const enterTenantInfoCondition = (
  loanCategory: Application['loan_category'],
  propertyCompanyUploadRentRollOrProvideTenantInfo: Application['system_power_rent_roll_or_tenant_info']
) => {
  return (
    [
      LoanCategory.EXPRESS_PROPCO,
      LoanCategory.CORE_PROPCO,
      LoanCategory.EXPRESS_PLUS_PROPCO,
    ].includes(loanCategory as LoanCategory) &&
    propertyCompanyUploadRentRollOrProvideTenantInfo ===
      'Enter information for a single tenant'
  );
};

const enterTenantLeaseTermCondition = (
  loanCategory: Application['loan_category'],
  propertyCompanyUploadRentRollOrProvideTenantInfo: Application['system_power_rent_roll_or_tenant_info']
) => {
  return (
    [
      LoanCategory.EXPRESS_PROPCO,
      LoanCategory.CORE_PROPCO,
      LoanCategory.EXPRESS_PLUS_PROPCO,
    ].includes(loanCategory as LoanCategory) &&
    propertyCompanyUploadRentRollOrProvideTenantInfo ===
      'Enter information for a single tenant'
  );
};

export const powerSoldToSingleUserCondition = (
  singleOrMultipleUsers: Application['system_power_single_or_multiple_users']
) => {
  return singleOrMultipleUsers === 'Single User';
};

export const powerIsCommunitySolarCondition = (
  singleOrMultipleUsers: Application['system_power_single_or_multiple_users'],
  communityOrPPALease: Application['community_solar_or_ppa_lease']
) => {
  return (
    singleOrMultipleUsers === 'Multiple Users' &&
    communityOrPPALease === 'Community Solar Arrangement'
  );
};

export const companyDocumentsValidation = {
  [ArticlesOfIncorporationValidation.name]:
    ArticlesOfIncorporationValidation.buildSchema(),
  [NonProfitBoardResolutionValidation.name]:
    NonProfitBoardResolutionValidation.buildSchema(),
  [ReligiousOrganizationAttendanceValidation.name]:
    ReligiousOrganizationAttendanceValidation.buildSchema(),
  [NonProfitBylawsCopyValidation.name]:
    NonProfitBylawsCopyValidation.buildSchema(),
  [PropertyCompanyUploadRentRollOrProvideTenantInfoValidation.name]:
    PropertyCompanyUploadRentRollOrProvideTenantInfoValidation.buildSchema(),
  [PropertyCompanyRentRollValidation.name]:
    PropertyCompanyRentRollValidation.buildSchema(),
  [PropertyCompanyTenantNameValidation.name]:
    PropertyCompanyTenantNameValidation.buildSchema(),
  [PropertyCompanyTenantMoveInYearValidation.name]:
    PropertyCompanyTenantMoveInYearValidation.buildSchema(),
  [PropertyCompanyTenantMonthlyRentPaymentValidation.name]:
    PropertyCompanyTenantMonthlyRentPaymentValidation.buildSchema(),
  [PropertyCompanyTenantRenewalYearValidation.name]:
    PropertyCompanyTenantRenewalYearValidation.buildSchema(),
  [PropertyCompanyTenantRenewalLengthValidation.name]:
    PropertyCompanyTenantRenewalLengthValidation.buildSchema(),
  [PropertyCompanyTenantPaymentsSatisfactoryValidation.name]:
    PropertyCompanyTenantPaymentsSatisfactoryValidation.buildSchema(),
  [PgMortgageStatementValidation.name]:
    PgMortgageStatementValidation.buildSchema(),
};

export const hoaDocumentsValidation = {
  [MemberAssessmentPolicyValidation.name]:
    MemberAssessmentPolicyValidation.buildSchema(),
  [PriorYearBudgetsValidation.name]: PriorYearBudgetsValidation.buildSchema(),
  [CurrentOccupantLedgerValidation.name]:
    CurrentOccupantLedgerValidation.buildSchema(),
  [MeetingMinutesValidation.name]: MeetingMinutesValidation.buildSchema(),
  [HoaBylawsValidation.name]: HoaBylawsValidation.buildSchema(),
};

export const solarSystemDocumentsValidation = {
  [LeaseOfTheSystemPropertyValidation.name]:
    LeaseOfTheSystemPropertyValidation.buildSchema(),
  // Single user
  [OfftakerPPAorLeaseAgreementValidation.name]:
    OfftakerPPAorLeaseAgreementValidation.buildSchema(),
  [ProofOfOfftakerCreditworthinessValidation.name]:
    ProofOfOfftakerCreditworthinessValidation.buildSchema(),
  [OffTakerLeaseOrProofOfOwnershipValidation.name]:
    OffTakerLeaseOrProofOfOwnershipValidation.buildSchema(),
  [OffTakerUtilityBillValidation.name]:
    OffTakerUtilityBillValidation.buildSchema(),
  [PpaLeaseFinancialProjectionsValidation.name]:
    PpaLeaseFinancialProjectionsValidation.buildSchema(),
  // Community solar
  [ApprovalForCommunitySolarDeliveryValidation.name]:
    ApprovalForCommunitySolarDeliveryValidation.buildSchema(),

  [CommunitySolarFinancialProjectionsValidation.name]:
    CommunitySolarFinancialProjectionsValidation.buildSchema(),
  [SubscriberManagementCompanyContractValidation.name]:
    SubscriberManagementCompanyContractValidation.buildSchema(),
  [PrincipalsValidation.name]: PrincipalsValidation.buildSchema(),
};

export const additionalDocumentationValidation = {
  [AdditionalDocumentsValidation.name]:
    AdditionalDocumentsValidation.buildSchema(),
};
